<script setup lang="ts">
import type { RelationBoxProps } from '@/types/appTypes'

const props = withDefaults(defineProps<RelationBoxProps>(), {
  event: false,
  header: '',
  relations: () => [],
  articles: () => [],
  eventList: null,
  pastEvent: false,
  pastDay: 0,
  sortType: null,
  sortVew: true,
  isPast: false,
  objects: null,
})

const app = useNuxtApp()
const items = ref([] as Article[])

/**
 * 詳細relationから関連アイテムを抽出
 * 対象Article Detail
 * event: present, report, isEvent, <jyunkai>, special
 * museum: (present), (report), event, isEvent, special
 * report: present, <jyunkai>, special?
 * special: report, museum, event
 * news: present, (report), event, museum
 * present: report
 * jyunkai: event, report
 *
 * ()は親が直接fetchし、articlesに渡す
 * <jyunkai>も親がfetchし、articlesに渡す
 * special?は使われてないので未実装
*/
if (props.relations.length) {
  items.value = []
  const newItems = []

  for (let i = 0; i < props.relations.length; i++) {
    const item = props.relations[i]
    if (item.article?.article_type_slug === props.article) {
      if (props.article === 'event') {
        const nDiff = app.$getPeriodDiffDay(item.article.term_from, item.article.term_to)
        if (props.pastEvent) {
          if (nDiff != null) {
            continue
          }
          if (props.pastDay > 0) {
            const nEndDay = app.$getPeriodEndDay(item.article.term_to)
            if (nEndDay) {
              if (nEndDay > props.pastDay) {
                continue
              }
            }
          }
        } else {
          if (nDiff === null && props.isPast === false) {
            continue
          }

          //  展覧会だけ、eventかどうか
          let isEvent = false
          if (item.article.custom.is_event === '1') {
            isEvent = true
          }
          if (props.event !== isEvent) {
            continue
          }
        }
      } else if (props.article === 'report') {
        if (props.isPast === false) {
          const tEvent = app.$getDetailRelationsEvent(props.eventList, item.article.article_slug)
          if (tEvent) {
            const nDiff = app.$getPeriodDiffDay(tEvent.termFrom, tEvent.termTo)
            if (nDiff === null) {
              continue
            }
          }
        }
      }
      const newItem = item.article
      newItems.push(newItem)
    }
  }

  // 指定が無い場合はslug毎に固定でソートする
  if (props.sortType === null) {
    if (props.article === 'event') {
      items.value = app.$sortArticleTermFrom(newItems, true)
    } else if (props.article === 'present' || props.article === 'report') {
      items.value = app.$sortArticleTermFromEventList(newItems, props.eventList, true)
    } else if (props.article === 'special' || props.article === 'museum') {
      items.value = app.$sortArticleOpenDate(newItems, true)
    } else {
      items.value = newItems
    }
    // ソート方法が指定されている場合は指定通りにソートする
  } else {
    switch (props.sortType) {
      case 'term_form':
        items.value = app.$sortArticleTermFrom(newItems, props.sortNew)
        break
      case 'term_form_event_list':
        items.value = app.$sortArticleTermFromEventList(newItems, props.eventList, props.sortNew)
        break
      case 'open_date':
        items.value = app.$sortArticleOpenDate(newItems, props.sortNew)
        break
    }
  }
} else if (props.articles.length) {
  items.value = props.articles
}
</script>

<template>
  <div
    v-if="items.length"
    class="c-relationBox"
  >
    <div class="c-relationBox_head">
      <img
        class="c-relationBox_head_icon"
        src="@/assets/images/icon-title.png"
      >
      <span
        class="c-relationBox_head_title"
      >
        {{ header }}
      </span>
    </div>
    <div class="c-relationBox_body">
      <template
        v-if="article === 'museum'"
      >
        <MuseumItem
          v-for="item in items"
          v-bind:key="item.id"
          v-bind:item="item"
        />
      </template>
      <template
        v-if="article === 'event'"
      >
        <EventItem
          v-for="item in items"
          v-bind:key="item.id"
          v-bind:item="item"
        />
      </template>
      <template
        v-if="article === 'report'"
      >
        <ReportItem
          v-for="item in items"
          v-bind:key="item.id"
          v-bind:item="item"
          v-bind:objects="objects"
          v-bind:hide-venue-period="true"
        />
      </template>
      <template
        v-if="article === 'present'"
      >
        <PresentItem
          v-for="item in items"
          v-bind:key="item.id"
          v-bind:item="item"
          class="c-relationBox_body_present"
        />
      </template>
      <template
        v-if="article === 'special'"
      >
        <SpecialItem
          v-for="item in items"
          v-bind:key="item.id"
          v-bind:item="item"
        />
      </template>
      <template
        v-if="article === 'jyunkai'"
      >
        <JyunkaiItem
          v-for="item in items"
          v-bind:key="item.id"
          v-bind:item="item"
        />
      </template>
      <template v-if="article === 'news'">
        <NewsCard
          v-for="item in items"
          v-bind:key="item.id"
          v-bind:item="item"
        />
      </template>
      <template v-if="article === 'gakugeiin'">
        <GakugeiinItem
          v-for="item in items"
          v-bind:key="item.id"
          v-bind:item="item"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive" as responsive;

.c-relationBox {
  margin-top: 83px;

  &_head {
    display: flex;
    align-items: flex-start;
    margin-bottom: 40px;
    padding-bottom: 12px;
    border-bottom: solid 1px var(--g_color_border_gray);

    &_icon {
      margin-top: 3px;
      margin-right: 15px;

      @include responsive.mq_down(sp) {
        width: 20px;
        height: 20px;
      }
    }

    &_title {
      display: inline-block;
      position: relative;
      width: 100%;
      margin-right: 10px;
      font-family: var(--g_font_family_SawarabiGothic);
      font-size: 20px;
      line-height: 1.5;

      @include responsive.mq_down(sp) {
        font-size: 18px;
      }
    }
  }

  &_body {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    &_present {
      margin-top: 40px;

      &:first-child {
        margin-top: 0;
      }
    }
  }

  :deep(div:first-child) {
    margin-top: 0;
  }

  :deep(.c-museumItem) {
    &:nth-child(2) {
      @include responsive.mq-up(pc) {
        margin-top: 0;
      }
    }
  }

  :deep(.c-eventItem) {
    &:nth-child(2) {
      @include responsive.mq-up(pc) {
        margin-top: 0;
      }
    }
  }

  .c-presentItem {
    padding: 20px;
    border: 1px solid var(--g_color_border_gray);

    @include responsive.mq_down(sp) {
      padding: 15px;
    }

    &_content_table {
      display: none;
    }
  }

  :deep(.c-jyunkaiItem) {
    padding: 20px;
    border: 1px solid var(--g_color_border_gray);

    @include responsive.mq_down(sp) {
      padding: 15px;
    }
  }
}
</style>
